import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AppLayout } from '@/components/layout/AppLayout';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { InvoicesTable } from '@/components/invoices/InvoicesTable';
import { ExportInvoicesModal } from '@/components/invoices/ExportInvoicesModal';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { invoicesService, InvoiceFilters } from '@/services/invoices.service';
import { clientsService } from '@/services/clients.service';
import { useToast } from '@/components/ui/Toast';
import type { Invoice, InvoiceStatus, Client } from '@/types';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, FileText, Search, X, RefreshCw, Download } from 'lucide-react';

function InvoicesContent() {
  const { toast } = useToast();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [updatingAccounting, setUpdatingAccounting] = useState<string | null>(null);
  const [deleteInvoice, setDeleteInvoice] = useState<Invoice | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  // Filters
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedClient, setSelectedClient] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadInvoices();
  }, [selectedStatus, selectedClient, startDate, endDate]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [invoicesData, clientsData] = await Promise.all([
        invoicesService.getInvoices(),
        clientsService.getClients(),
      ]);
      setInvoices(invoicesData);
      setClients(clientsData);
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(err.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const loadInvoices = async () => {
    try {
      setError(null);
      const filters: InvoiceFilters = {};
      if (selectedStatus && selectedStatus !== 'all') filters.status = selectedStatus;
      if (selectedClient && selectedClient !== 'all') filters.clientId = selectedClient;
      if (startDate) filters.startDate = startDate;
      if (endDate) filters.endDate = endDate;

      const data = await invoicesService.getInvoices(filters);
      setInvoices(data);
    } catch (err: any) {
      console.error('Error loading invoices:', err);
      setError(err.message || 'Failed to load invoices');
    }
  };

  // Filter by search query (invoice number)
  const filteredInvoices = searchQuery
    ? invoices.filter((invoice) =>
        invoice.invoiceNumber.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : invoices;

  const handleDelete = async () => {
    if (!deleteInvoice) return;
    try {
      setIsDeleting(true);
      await invoicesService.deleteInvoice(deleteInvoice.id);
      await loadInvoices();
      toast.success('Invoice deleted successfully');
    } catch (err: any) {
      console.error('Error deleting invoice:', err);
      toast.error(err.message || 'Failed to delete invoice');
    } finally {
      setIsDeleting(false);
      setDeleteInvoice(null);
    }
  };

  const handleViewPdf = async (id: string) => {
    try {
      const result = await invoicesService.generateInvoicePdf(id);
      window.open(result.pdfUrl, '_blank');
    } catch (err: any) {
      console.error('Error generating PDF:', err);
      toast.error(err.message || 'Failed to generate PDF');
    }
  };

  const handleDownloadPdf = async (id: string, invoiceNumber: string) => {
    try {
      const result = await invoicesService.generateInvoicePdf(id);
      const response = await fetch(result.pdfUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (err: any) {
      console.error('Error downloading PDF:', err);
      toast.error(err.message || 'Failed to download PDF');
    }
  };

  const handleStatusChange = async (invoiceId: string, currentStatus: InvoiceStatus, newStatus: InvoiceStatus) => {
    if (currentStatus === newStatus) return;

    try {
      setUpdatingStatus(invoiceId);

      if (newStatus === 'sent') {
        await invoicesService.sendInvoice(invoiceId);
      } else if (newStatus === 'paid') {
        const invoice = invoices.find(inv => inv.id === invoiceId);
        if (invoice) {
          await invoicesService.payInvoice(invoiceId, {
            paidAmount: invoice.total,
            paidDate: new Date().toISOString().split('T')[0],
            paymentMethod: 'bank_transfer',
          });
        }
      }

      await loadInvoices();
      toast.success('Invoice status updated');
    } catch (err: any) {
      console.error('Error updating status:', err);
      toast.error(err.message || 'Failed to update invoice status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleToggleSentToAccounting = async (invoiceId: string, sentToAccounting: boolean) => {
    try {
      setUpdatingAccounting(invoiceId);
      const updated = await invoicesService.setSentToAccounting(invoiceId, sentToAccounting);
      // Update the single invoice in place to avoid a full reload / flicker
      setInvoices((prev) =>
        prev.map((inv) => (inv.id === invoiceId ? { ...inv, ...updated } : inv))
      );
      toast.success(
        sentToAccounting ? 'Marked as sent to accounting' : 'Unmarked as sent to accounting'
      );
    } catch (err: any) {
      console.error('Error updating sent-to-accounting flag:', err);
      toast.error(err.message || 'Failed to update invoice');
    } finally {
      setUpdatingAccounting(null);
    }
  };

  const activeFiltersCount = [
    selectedStatus !== 'all',
    selectedClient !== 'all',
    !!startDate,
    !!endDate,
  ].filter(Boolean).length;

  const clearAllFilters = () => {
    setSelectedStatus('all');
    setSelectedClient('all');
    setStartDate('');
    setEndDate('');
  };

  const emptyState = (
    <div className="text-center py-12 border border-border rounded-lg">
      <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
      <h3 className="text-lg font-semibold text-foreground mb-2">No invoices found</h3>
      <p className="text-muted-foreground mb-4">
        {searchQuery || selectedStatus !== 'all' || selectedClient !== 'all' || startDate || endDate
          ? 'Try adjusting your filters'
          : 'Get started by creating a new invoice'}
      </p>
      {!searchQuery && selectedStatus === 'all' && selectedClient === 'all' && !startDate && !endDate && (
        <Button asChild>
          <Link to="/finances/invoices/new">
            <Plus className="w-4 h-4 mr-2" />
            Create Invoice
          </Link>
        </Button>
      )}
    </div>
  );

  return (
    <AppLayout>
      <div className="mb-6">
        <Breadcrumb items={[{ label: 'Finances', href: '/finances' }, { label: 'Invoices' }]} />
        <h1 className="text-2xl font-bold text-foreground">Invoices</h1>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
          {error}
        </div>
      )}

      {loading ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-96" />
        </div>
      ) : (
        <>
          {/* Quick Actions */}
          <div className="mb-6 rounded-lg border border-border p-4">
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Quick Actions
            </h2>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setExportOpen(true)}
                className="flex items-center gap-3 rounded-lg border border-border px-4 py-3 text-left transition-colors hover:bg-muted"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Download className="h-5 w-5" />
                </span>
                <span className="text-sm font-medium text-foreground">Export</span>
              </button>
            </div>
          </div>

          {/* Toolbar - Inline search and filters */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            {/* Search */}
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search invoices..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Status Filter */}
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            {/* Client Filter */}
            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Clients" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Date Filters */}
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-[140px]"
              placeholder="Start date"
            />
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-[140px]"
              placeholder="End date"
            />

            {/* Spacer */}
            <div className="flex-1" />

            {/* Add Button */}
            <Button asChild>
              <Link to="/finances/invoices/new">
                <Plus className="w-4 h-4" />
              </Link>
            </Button>

            {/* Refresh Button */}
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={loadData}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>

          {/* Active Filters */}
          {activeFiltersCount > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="text-sm text-muted-foreground">Active filters:</span>

              {selectedStatus !== 'all' && (
                <Badge
                  variant="secondary"
                  className="cursor-pointer"
                  onClick={() => setSelectedStatus('all')}
                >
                  Status: {selectedStatus.charAt(0).toUpperCase() + selectedStatus.slice(1)}
                  <X className="w-3 h-3 ml-1" />
                </Badge>
              )}

              {selectedClient !== 'all' && (
                <Badge
                  variant="secondary"
                  className="cursor-pointer"
                  onClick={() => setSelectedClient('all')}
                >
                  Client: {clients.find((c) => c.id === selectedClient)?.name}
                  <X className="w-3 h-3 ml-1" />
                </Badge>
              )}

              {startDate && (
                <Badge
                  variant="secondary"
                  className="cursor-pointer"
                  onClick={() => setStartDate('')}
                >
                  From: {startDate}
                  <X className="w-3 h-3 ml-1" />
                </Badge>
              )}

              {endDate && (
                <Badge
                  variant="secondary"
                  className="cursor-pointer"
                  onClick={() => setEndDate('')}
                >
                  To: {endDate}
                  <X className="w-3 h-3 ml-1" />
                </Badge>
              )}

              {activeFiltersCount > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllFilters}
                  className="text-muted-foreground hover:text-foreground"
                >
                  Clear all
                </Button>
              )}
            </div>
          )}

          {/* Content */}
          <InvoicesTable
            invoices={filteredInvoices}
            clients={clients}
            isLoading={loading}
            updatingStatus={updatingStatus}
            updatingAccounting={updatingAccounting}
            onDelete={setDeleteInvoice}
            onStatusChange={handleStatusChange}
            onToggleSentToAccounting={handleToggleSentToAccounting}
            onViewPdf={handleViewPdf}
            onDownloadPdf={handleDownloadPdf}
            emptyState={emptyState}
          />
        </>
      )}

      {/* Export Invoices Modal */}
      <ExportInvoicesModal open={exportOpen} onOpenChange={setExportOpen} />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteInvoice}
        onClose={() => setDeleteInvoice(null)}
        onConfirm={handleDelete}
        title="Delete Invoice"
        description={`Are you sure you want to delete invoice "${deleteInvoice?.invoiceNumber}"? This action cannot be undone.`}
        confirmText={isDeleting ? 'Deleting...' : 'Delete'}
        cancelText="Cancel"
        type="danger"
      />
    </AppLayout>
  );
}

export default function InvoicesPage() {
  return (
    <ProtectedRoute>
      <InvoicesContent />
    </ProtectedRoute>
  );
}
